/* eslint-disable no-unused-vars */
// =============================================================================
// CreateCampaignScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/CreateCampaignScreen.js
// Receives optional: route.params.duplicateFrom (campaign to copy)
//
// FIXED: removed leftover Alert.alert("DEBUG") · removed fake "posted to
//        social platforms" flow (offers real AI launch content instead) ·
//        objective/channels/audience now saved to real columns (run the
//        ALTER in the chat message once) with extras kept in metrics JSON.
// Live DB: POST /api/data/campaigns · DB badge (/health + table ping)
// 5 steps: Basics (AI description writer) · Channels · Budget · Schedule ·
//          Review & create. Validation per step, duplicate support,
//          launch-now vs save-as-draft choice.
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API ─────────────────────────────────────────────────────────────────────

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getBid = async () =>
  parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;

// ─── Constants ───────────────────────────────────────────────────────────────

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#7C3AED',
  teal: '#0891B2',
};

const TOTAL_STEPS = 5;
const STEP_TITLES = ['Basics', 'Channels', 'Budget', 'Schedule', 'Review'];

const CAMPAIGN_TYPES = ['Recruitment', 'Brand Awareness', 'Lead Generation', 'Event', 'Product Launch', 'Re-engagement'];

const OBJECTIVES = [
  'Candidate Attraction',
  'Lead Generation',
  'Brand Awareness',
  'Client Acquisition',
  'Event Promotion',
  'Re-engagement',
];

const CHANNELS = [
  { key: 'email', label: 'Email', icon: 'email-outline', color: C.teal },
  { key: 'social', label: 'Social Media', icon: 'share-variant-outline', color: C.accent },
  { key: 'job_board', label: 'Job Boards', icon: 'briefcase-outline', color: C.primary },
  { key: 'sms', label: 'SMS', icon: 'message-text-outline', color: C.success },
  { key: 'ppc', label: 'Paid Ads (PPC)', icon: 'cursor-default-click-outline', color: C.warning },
  { key: 'content', label: 'Content / Blog', icon: 'post-outline', color: C.purple },
];

const isValidDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d);
const money = (n) => `£${(Number(n) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const parseChannels = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const j = JSON.parse(raw); if (Array.isArray(j)) return j; } catch {}
    return raw.split(',').map((x) => x.trim()).filter(Boolean);
  }
  return [];
};

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const Field = React.memo(({ label, value, onChangeText, placeholder, multiline, keyboardType, rightSlot }) => (
  <View style={s.fieldWrap}>
    <View style={s.fieldLabelRow}>
      <Text style={s.fieldLabel}>{label}</Text>
      {rightSlot || null}
    </View>
    <TextInput
      style={[s.input, multiline && s.inputMultiline]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#9CA3AF"
      multiline={!!multiline}
      keyboardType={keyboardType || 'default'}
    />
  </View>
));

const Chip = React.memo(({ label, active, onPress }) => (
  <TouchableOpacity style={[s.chip, active && s.chipActive]} onPress={onPress} activeOpacity={0.7}>
    <Text style={[s.chipText, active && s.chipTextActive]}>{label}</Text>
  </TouchableOpacity>
));

const ChannelCard = React.memo(({ channel, active, onPress }) => (
  <TouchableOpacity
    style={[s.channelCard, active && { backgroundColor: channel.color, borderColor: channel.color }]}
    onPress={() => onPress(channel.key)}
    activeOpacity={0.7}
  >
    <MaterialCommunityIcons name={channel.icon} size={22} color={active ? '#fff' : channel.color} />
    <Text style={[s.channelLabel, active && { color: '#fff' }]}>{channel.label}</Text>
    {active && <Ionicons name="checkmark-circle" size={15} color="#fff" style={{ position: 'absolute', top: 6, right: 6 }} />}
  </TouchableOpacity>
));

const Toggle = React.memo(({ label, sub, value, onChange }) => (
  <TouchableOpacity style={s.toggleRow} onPress={() => onChange(!value)} activeOpacity={0.7}>
    <View style={{ flex: 1, marginRight: 10 }}>
      <Text style={s.toggleLabel}>{label}</Text>
      {!!sub && <Text style={s.toggleSub}>{sub}</Text>}
    </View>
    <View style={[s.toggleTrack, value && { backgroundColor: C.success }]}>
      <View style={[s.toggleThumb, value && { alignSelf: 'flex-end' }]} />
    </View>
  </TouchableOpacity>
));

const ReviewRow = React.memo(({ label, value }) => (
  <View style={s.reviewRow}>
    <Text style={s.reviewLbl}>{label}</Text>
    <Text style={s.reviewVal} numberOfLines={2}>{value}</Text>
  </View>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const CreateCampaignScreen = ({ navigation, route }) => {
  const dup = route?.params?.duplicateFrom || null;

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1 — Basics
  const [name, setName] = useState(dup?.name ? `Copy of ${dup.name}` : '');
  const [description, setDescription] = useState(dup?.description || '');
  const [type, setType] = useState(dup?.type || 'Recruitment');
  const [objective, setObjective] = useState(dup?.objective || 'Candidate Attraction');
  const [targetAudience, setTargetAudience] = useState(dup?.target_audience || dup?.targetAudience || '');
  const [aiWriting, setAiWriting] = useState(false);

  // Step 2 — Channels
  const [channels, setChannels] = useState(() => parseChannels(dup?.channels || dup?.platforms) || []);

  // Step 3 — Budget
  const [budget, setBudget] = useState(dup?.budget ? String(dup.budget) : '');
  const [dailyLimit, setDailyLimit] = useState(false);
  const [dailyBudget, setDailyBudget] = useState('');

  // Step 4 — Schedule
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(
    new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
  );
  const [runContinuous, setRunContinuous] = useState(false);

  // Step 5 — Review
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [launchNow, setLaunchNow] = useState(false);

  // DB
  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  // ─── DB CHECK ────────────────────────────────────────────────────────────

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/campaigns?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + campaigns table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, campaigns table unreachable'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  useEffect(() => { checkDb(); }, [checkDb]);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ─── AI DESCRIPTION ──────────────────────────────────────────────────────

  const aiWriteDescription = useCallback(async () => {
    if (!name.trim()) { Alert.alert('Name first', 'Enter the campaign name so AI has context.'); return; }
    setAiWriting(true);
    try {
      const h = await getH();
      const chLabels = channels.map((k) => CHANNELS.find((c) => c.key === k)?.label || k).join(', ');
      const prompt =
        `Write a concise, professional marketing campaign description (60-90 words, plain text, British English, no markdown) for:\n` +
        `Name: ${name}\nType: ${type}\nObjective: ${objective}\n` +
        (targetAudience ? `Target audience: ${targetAudience}\n` : '') +
        (chLabels ? `Channels: ${chLabels}\n` : '');
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      if (!res?.content) throw new Error(res?.message || 'No response');
      setDescription(String(res.content).trim());
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not write description.');
    } finally { setAiWriting(false); }
  }, [name, type, objective, targetAudience, channels]);

  // ─── VALIDATION + NAV ────────────────────────────────────────────────────

  const validateStep = useCallback(() => {
    switch (step) {
      case 1:
        if (!name.trim()) { Alert.alert('Required', 'Enter a campaign name.'); return false; }
        return true;
      case 2:
        if (!channels.length) { Alert.alert('Required', 'Select at least one channel.'); return false; }
        return true;
      case 3:
        if (!budget || parseFloat(budget) <= 0) { Alert.alert('Required', 'Enter a budget greater than zero.'); return false; }
        if (dailyLimit && (!dailyBudget || parseFloat(dailyBudget) <= 0)) { Alert.alert('Required', 'Enter the daily budget or turn the limit off.'); return false; }
        return true;
      case 4:
        if (!isValidDate(startDate)) { Alert.alert('Invalid date', 'Start date must be YYYY-MM-DD.'); return false; }
        if (!runContinuous) {
          if (!isValidDate(endDate)) { Alert.alert('Invalid date', 'End date must be YYYY-MM-DD.'); return false; }
          if (endDate <= startDate) { Alert.alert('Invalid dates', 'End date must be after the start date.'); return false; }
        }
        return true;
      default:
        return true;
    }
  }, [step, name, channels, budget, dailyLimit, dailyBudget, startDate, endDate, runContinuous]);

  const next = useCallback(() => { if (validateStep()) setStep((x) => Math.min(TOTAL_STEPS, x + 1)); }, [validateStep]);
  const prev = useCallback(() => setStep((x) => Math.max(1, x - 1)), []);

  // ─── SUBMIT ──────────────────────────────────────────────────────────────

  const submit = useCallback(async () => {
    if (!validateStep()) return;
    const bid = await getBid();
    if (!bid) { Alert.alert('No company', 'Company context missing — sign in again.'); return; }
    setSaving(true);
    try {
      const h = await getH();
      const payload = {
        company_id: bid,
        name: name.trim(),
        description: description.trim() || null,
        type,
        objective,
        channels: JSON.stringify(channels),
        target_audience: targetAudience.trim() || null,
        budget: parseFloat(budget) || 0,
        spent: 0,
        impressions: 0,
        clicks: 0,
        conversions: 0,
        start_date: startDate,
        end_date: runContinuous ? null : endDate,
        status: launchNow ? 'active' : 'draft',
        metrics: JSON.stringify({
          daily_budget: dailyLimit ? parseFloat(dailyBudget) || 0 : null,
          currency: 'GBP',
          tags: tags.trim(),
          notes: notes.trim(),
          run_continuous: runContinuous,
        }),
      };

      const res = await fetch(`${API_BASE}/api/data/campaigns`, {
        method: 'POST', headers: h, body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      const savedToServer = res.ok && json?.success !== false;
      const newCampaign = json?.data || { ...payload, id: null };

      // Cache
      const cached = JSON.parse((await AsyncStorage.getItem('campaigns_cache').catch(() => '[]')) || '[]');
      await AsyncStorage.setItem('campaigns_cache', JSON.stringify([newCampaign, ...cached].slice(0, 50)));

      setSaving(false);

      if (!savedToServer) {
        Alert.alert(
          '⚠️ Saved locally',
          `Server rejected the campaign (HTTP ${res.status}). It was cached on this device.\n\n${JSON.stringify(json).slice(0, 160)}`,
          [{ text: 'OK' }]
        );
        return;
      }

      Alert.alert(
        launchNow ? '🚀 Campaign live' : '✅ Campaign created',
        `"${name}" ${launchNow ? 'is now active' : 'was saved as a draft'}.`,
        [
          {
            text: '✨ Create launch content',
            onPress: () => navigation.replace('CampaignAIContentAssistantScreen', {
              campaign: newCampaign, campaignId: newCampaign.id, contentType: 'social',
            }),
          },
          {
            text: 'Open campaign',
            onPress: () => navigation.replace('CampaignDetails', {
              campaign: newCampaign, campaignId: newCampaign.id,
            }),
          },
        ]
      );
    } catch (e) {
      setSaving(false);
      Alert.alert('Error', e?.message || 'Could not create the campaign.');
    }
  }, [validateStep, name, description, type, objective, channels, targetAudience, budget,
      dailyLimit, dailyBudget, startDate, endDate, runContinuous, tags, notes, launchNow, navigation]);

  // ─── DERIVED ─────────────────────────────────────────────────────────────

  const durationDays = useMemo(() => {
    if (runContinuous || !isValidDate(startDate) || !isValidDate(endDate)) return null;
    const d = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
    return d > 0 ? d : null;
  }, [startDate, endDate, runContinuous]);

  const dailySuggested = useMemo(() => {
    const b = parseFloat(budget) || 0;
    if (!b || !durationDays) return null;
    return b / durationDays;
  }, [budget, durationDays]);

  const toggleChannel = useCallback((key) => {
    setChannels((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }, []);

  // ─── STEPS ───────────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <View>
      <Text style={s.stepTitle}>Campaign basics</Text>
      <Text style={s.stepDesc}>What is this campaign and who is it for?</Text>

      <Field label="Campaign name *" value={name} onChangeText={setName} placeholder="e.g. Spring engineer hiring push" />

      <Text style={s.fieldLabel}>Type</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
        {CAMPAIGN_TYPES.map((tp) => (
          <Chip key={tp} label={tp} active={type === tp} onPress={() => setType(tp)} />
        ))}
      </ScrollView>

      <Text style={s.fieldLabel}>Objective</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
        {OBJECTIVES.map((o) => (
          <Chip key={o} label={o} active={objective === o} onPress={() => setObjective(o)} />
        ))}
      </ScrollView>

      <Field
        label="Target audience"
        value={targetAudience}
        onChangeText={setTargetAudience}
        placeholder="e.g. Civil engineers, London, 3-8 yrs experience"
      />

      <Field
        label="Description"
        value={description}
        onChangeText={setDescription}
        placeholder="What is this campaign about?"
        multiline
        rightSlot={
          <TouchableOpacity style={s.aiWriteBtn} onPress={aiWriteDescription} disabled={aiWriting}>
            {aiWriting ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="brain" size={14} color={C.accent} />}
            <Text style={s.aiWriteText}>{aiWriting ? 'Writing…' : 'AI write'}</Text>
          </TouchableOpacity>
        }
      />
    </View>
  );

  const renderStep2 = () => (
    <View>
      <Text style={s.stepTitle}>Channels</Text>
      <Text style={s.stepDesc}>Where will this campaign run? ({channels.length} selected)</Text>
      <View style={s.channelGrid}>
        {CHANNELS.map((ch) => (
          <ChannelCard key={ch.key} channel={ch} active={channels.includes(ch.key)} onPress={toggleChannel} />
        ))}
      </View>
      <View style={s.infoBox}>
        <Ionicons name="information-circle-outline" size={15} color={C.primary} />
        <Text style={s.infoText}>
          After creating the campaign you can generate channel-specific content with the AI assistant — posts, emails, ad copy and job adverts sized for each platform.
        </Text>
      </View>
    </View>
  );

  const renderStep3 = () => (
    <View>
      <Text style={s.stepTitle}>Budget</Text>
      <Text style={s.stepDesc}>How much will this campaign spend?</Text>

      <Field label="Total budget (£) *" value={budget} onChangeText={(tx) => setBudget(tx.replace(/[^0-9.]/g, ''))} placeholder="e.g. 1500" keyboardType="numeric" />

      <Toggle label="Daily spending limit" sub="Cap how much can be spent per day" value={dailyLimit} onChange={setDailyLimit} />
      {dailyLimit && (
        <Field label="Daily budget (£)" value={dailyBudget} onChangeText={(tx) => setDailyBudget(tx.replace(/[^0-9.]/g, ''))} placeholder="e.g. 50" keyboardType="numeric" />
      )}
      {dailySuggested && (
        <View style={s.infoBox}>
          <MaterialCommunityIcons name="calculator-variant-outline" size={15} color={C.primary} />
          <Text style={s.infoText}>
            {money(budget)} over {durationDays} days ≈ {money(dailySuggested)} per day.
          </Text>
        </View>
      )}
    </View>
  );

  const renderStep4 = () => (
    <View>
      <Text style={s.stepTitle}>Schedule</Text>
      <Text style={s.stepDesc}>When does the campaign run?</Text>

      <Field label="Start date *" value={startDate} onChangeText={setStartDate} placeholder="YYYY-MM-DD" />
      <Toggle label="Run continuously" sub="No end date — pause it manually when done" value={runContinuous} onChange={setRunContinuous} />
      {!runContinuous && (
        <Field label="End date *" value={endDate} onChangeText={setEndDate} placeholder="YYYY-MM-DD" />
      )}
      {durationDays && (
        <View style={s.infoBox}>
          <Ionicons name="calendar-outline" size={15} color={C.primary} />
          <Text style={s.infoText}>Campaign duration: {durationDays} day{durationDays === 1 ? '' : 's'}.</Text>
        </View>
      )}
    </View>
  );

  const renderStep5 = () => (
    <View>
      <Text style={s.stepTitle}>Review & create</Text>
      <Text style={s.stepDesc}>Check everything before creating.</Text>

      <View style={s.reviewCard}>
        <ReviewRow label="Name" value={name || '—'} />
        <ReviewRow label="Type" value={type} />
        <ReviewRow label="Objective" value={objective} />
        {!!targetAudience && <ReviewRow label="Audience" value={targetAudience} />}
        <ReviewRow label="Channels" value={channels.map((k) => CHANNELS.find((c) => c.key === k)?.label || k).join(', ') || '—'} />
        <ReviewRow label="Budget" value={money(budget)} />
        {dailyLimit && <ReviewRow label="Daily limit" value={money(dailyBudget)} />}
        <ReviewRow label="Schedule" value={runContinuous ? `${startDate} → ongoing` : `${startDate} → ${endDate}`} />
      </View>

      <Field label="Tags (optional)" value={tags} onChangeText={setTags} placeholder="e.g. spring, engineering, hiring" />
      <Field label="Internal notes (optional)" value={notes} onChangeText={setNotes} placeholder="Notes for your team…" multiline />

      <Toggle
        label="Launch immediately"
        sub={launchNow ? 'Campaign will be created as Active' : 'Campaign will be saved as a Draft'}
        value={launchNow}
        onChange={setLaunchNow}
      />
    </View>
  );

  // ─── RENDER ──────────────────────────────────────────────────────────────

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => (step > 1 ? prev() : navigation.goBack())}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>{dup ? 'Duplicate Campaign' : 'New Campaign'}</Text>
          <Text style={s.headerSub}>Step {step} of {TOTAL_STEPS} · {STEP_TITLES[step - 1]}</Text>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
      </View>

      {/* Step indicator */}
      <View style={s.stepIndicator}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <React.Fragment key={i}>
            <View style={[s.stepDot, step > i + 1 && s.stepDotDone, step === i + 1 && s.stepDotActive]}>
              {step > i + 1
                ? <Ionicons name="checkmark" size={12} color="#fff" />
                : <Text style={[s.stepDotText, step === i + 1 && { color: C.primary }]}>{i + 1}</Text>}
            </View>
            {i < TOTAL_STEPS - 1 && <View style={[s.stepLine, step > i + 1 && s.stepLineDone]} />}
          </React.Fragment>
        ))}
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={s.body} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          <View style={{ height: 24 }} />
        </ScrollView>

        {/* Footer */}
        <View style={s.footer}>
          {step > 1 && (
            <TouchableOpacity style={s.backBtn} onPress={prev}>
              <Text style={s.backBtnText}>Back</Text>
            </TouchableOpacity>
          )}
          {step < TOTAL_STEPS ? (
            <TouchableOpacity style={s.nextBtn} onPress={next}>
              <Text style={s.nextBtnText}>Continue</Text>
              <Ionicons name="arrow-forward" size={17} color="#fff" />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity style={[s.nextBtn, { backgroundColor: C.success }, saving && { opacity: 0.6 }]} onPress={submit} disabled={saving}>
              {saving ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="rocket-launch-outline" size={17} color="#fff" />}
              <Text style={s.nextBtnText}>{saving ? 'Creating…' : launchNow ? 'Create & Launch' : 'Create Campaign'}</Text>
            </TouchableOpacity>
          )}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 8 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 1 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  stepIndicator: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: C.primary, paddingBottom: 14, paddingHorizontal: 24 },
  stepDot: { width: 26, height: 26, borderRadius: 13, backgroundColor: 'rgba(255,255,255,0.18)', alignItems: 'center', justifyContent: 'center' },
  stepDotActive: { backgroundColor: '#fff' },
  stepDotDone: { backgroundColor: C.success },
  stepDotText: { fontSize: 11.5, fontWeight: '800', color: 'rgba(255,255,255,0.8)' },
  stepLine: { flex: 1, height: 2.5, backgroundColor: 'rgba(255,255,255,0.18)', marginHorizontal: 4 },
  stepLineDone: { backgroundColor: C.success },

  body: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 16, paddingTop: 16 },

  stepTitle: { fontSize: 19, fontWeight: '800', color: C.text },
  stepDesc: { fontSize: 12.5, color: C.sub, marginTop: 4, marginBottom: 14 },

  fieldWrap: { marginBottom: 13 },
  fieldLabelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  fieldLabel: { fontSize: 12.5, fontWeight: '700', color: C.text, marginBottom: 7, marginTop: 4 },
  input: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 11, paddingHorizontal: 13, paddingVertical: Platform.OS === 'ios' ? 11 : 9, fontSize: 14, color: C.text },
  inputMultiline: { minHeight: 90, textAlignVertical: 'top' },

  chipRow: { gap: 8, paddingVertical: 2, marginBottom: 8 },
  chip: { paddingHorizontal: 13, paddingVertical: 8, borderRadius: 16, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12.5, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  aiWriteBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#FCE7F3', paddingHorizontal: 9, paddingVertical: 4, borderRadius: 8 },
  aiWriteText: { fontSize: 11, fontWeight: '700', color: C.accent },

  channelGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 9 },
  channelCard: { width: '47%', flexGrow: 1, alignItems: 'center', backgroundColor: C.card, borderRadius: 13, paddingVertical: 17, borderWidth: 1.5, borderColor: C.border },
  channelLabel: { fontSize: 12, fontWeight: '700', color: C.text, marginTop: 7, textAlign: 'center' },

  infoBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 7, backgroundColor: '#EFF4FF', borderRadius: 11, padding: 11, marginTop: 12 },
  infoText: { flex: 1, fontSize: 11.5, color: C.primary, lineHeight: 16, fontWeight: '600' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: C.card, borderRadius: 11, padding: 13, borderWidth: 1, borderColor: C.border, marginBottom: 12, marginTop: 2 },
  toggleLabel: { fontSize: 13.5, fontWeight: '700', color: C.text },
  toggleSub: { fontSize: 11, color: C.sub, marginTop: 2 },
  toggleTrack: { width: 42, height: 24, borderRadius: 12, backgroundColor: '#CBD5E1', padding: 3 },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: '#fff' },

  reviewCard: { backgroundColor: C.card, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: C.border, marginBottom: 14 },
  reviewRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, gap: 12 },
  reviewLbl: { fontSize: 12.5, color: C.sub, fontWeight: '600' },
  reviewVal: { fontSize: 12.5, color: C.text, fontWeight: '700', flexShrink: 1, textAlign: 'right' },

  footer: { flexDirection: 'row', gap: 10, padding: 14, backgroundColor: C.card, borderTopWidth: 1, borderTopColor: C.border },
  backBtn: { paddingHorizontal: 20, justifyContent: 'center', borderRadius: 12, backgroundColor: '#F1F5F9' },
  backBtnText: { fontSize: 14, fontWeight: '700', color: C.sub },
  nextBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: C.primary, borderRadius: 12, paddingVertical: 14 },
  nextBtnText: { color: '#fff', fontSize: 15, fontWeight: '800' },
});

export default CreateCampaignScreen;
